import { INGEST_SOURCE_LABELS } from "@models/IngestRun.model";
import React, { ReactNode, useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Paths } from "src/paths";
import {
  Alert,
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  LinearProgress,
} from "@mui/material";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import DownloadIcon from "@mui/icons-material/Download";
import GppGoodIcon from "@mui/icons-material/GppGood";
import ScienceIcon from "@mui/icons-material/Science";

import { appStore } from "@store/appStore";
import { IngestDefinitionService } from "@services/ingest/ingestDefinitionService.ts";
import { IngestFileService } from "@services/ingest/ingestFileService.ts";
import { CommandGetJob } from "@services/jobs/commands/cmdGetJob";
import { CommandListJobs } from "@services/jobs/commands/cmdListJobs";
import type { IngestRun, IngestRunStatus } from "@models/IngestRun.model";
import type { RunVersionPreview } from "@models/configVersion.model";
import RunVersionPreviewDialog from "./RunVersionPreviewDialog";
import { useReprocessAll } from "./useReprocessAll";
import type { SegmentLinkLaunch } from "@services/engagements/commands/cmdGetSegmentLinks";
import { hasLoadWarnings, hasPendingRuns, hasRunnableRuns, needsRebuild } from "./fileRunState";
import { useDocumentTitle } from "@/shared/hooks/useDocumentTitle";
import { rowHoverLiftSx, liftableTableSx } from "@/shared/styles/rowHoverLift";
import { preflightUploadFile, describeStorageFailure, describePresignFailure } from "@services/ingest/uploadHelpers";
import HistoryButton from "@components/versions/HistoryButton";
import AggregateVersionsDrawer, {
  AggregateActivityEntry,
} from "@components/versions/AggregateVersionsDrawer";
import type { AggregateVersion } from "@models/aggregateVersion.model";

const STATUS_COLORS: Record<IngestRunStatus, string> = {
  uploaded: "#845adf",
  upload_failed: "#e6533c",
  validating: "#f5b849",
  validated: "#26bf94",
  validation_failed: "#e6533c",
  running: "#f5b849",
  completed: "#536485",
  failed: "#e6533c",
};

// The file's own triggers, on top of the three every domain has. Read with `changed_by`,
// never instead of it: the System user authors the SFTP deliveries, the parks and the purges,
// and "by System" alone explains none of them.
const FILE_TRIGGER_LABELS: Record<string, string> = {
  uploaded: "uploaded the file",
  sftp_delivery: "delivered over SFTP",
  migrated: "migrated from the old platform",
  upload_failed: "upload rejected",
  validated: "validated",
  submitted: "submitted for processing",
  discarded: "discarded",
  parked: "held for the file it depends on",
  released: "released",
  definition_unlinked: "unlinked from its config",
  purged: "source file deleted by retention",
};

const STATUS_LABELS: Record<IngestRunStatus, string> = {
  uploaded: "Uploaded",
  upload_failed: "Upload failed",
  validating: "Validating...",
  validated: "Validated",
  validation_failed: "Validation Failed",
  running: "Running...",
  completed: "Completed",
  failed: "Failed",
};

// "Emails: 0 of 5,247 rows" reads at a glance; "email" alone does not (MPN-1969).
const CONTACT_KIND_LABELS: Record<string, string> = { email: "Emails", phone: "Phones" };

interface DefinitionInfo {
  id: number;
  name: string;
  organization_id: number | null;
  population_name: string | null;
}

const fileService = new IngestFileService();

interface PanelProps {
  definitionId: number;
  organizationId: number;
  /** This population's campaigns, for the go-live strip (MPN-1675). Omitted on the
   *  standalone definitions route, which has no engagement to read them from. */
  launches?: SegmentLinkLaunch[];
  /** The population this definition feeds. "Reprocess all files" rebuilds the whole
   *  population — every data spec feeding it — so the button names it (MPN-1815). */
  populationName?: string;
  /** Header row above the drop zone. Omitted → none (embedded hosts own their chrome). */
  headerSlot?: ReactNode;
  /** The population page owns "Reprocess all files" in its own header (MPN-1945). */
  hideReprocessAll?: boolean;
  /** Fired after any write that can change setup status (upload, validate, run). */
  onMutated?: () => void;
}

/*
    Upload + run surface for one ingest definition: drop zone, run history, the
    per-run validate/run/discard/download actions, and bulk "process pending"
    (the browser version of catch_up_launch). Shared by the standalone Ingest
    Files page and the engagement Data File item.
*/
export function IngestFilesPanel({
  definitionId,
  organizationId,
  launches,
  populationName,
  headerSlot,
  hideReprocessAll = false,
  onMutated,
}: PanelProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  // Named in the button and the confirm so nobody reads "reprocess" as this data
  // spec's files alone: the job rebuilds the population across every spec feeding it.
  const populationLabel = populationName ? `"${populationName}"` : "this population";

  const [files, setFiles] = useState<IngestRun[]>([]);
  const [uploading, setUploading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [processJob, setProcessJob] = useState<{ id: number; status: string } | null>(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [versions, setVersions] = useState<AggregateVersion[] | null>(null);
  const [activity, setActivity] = useState<AggregateActivityEntry[]>([]);
  const [historyError, setHistoryError] = useState(false);
  const [processMessage, setProcessMessage] = useState<string | null>(null);
  // Which action owns the running job, so each button reports its own state
  // instead of "Process pending files" claiming a reprocess, and so the status
  // line uses the right verb.
  const [processKind, setProcessKind] = useState<"pending" | "reprocess" | null>(null);

  const [startedAt, setStartedAt] = useState<number | null>(null);
  // Server-reported progress (both batch processors now call update_job_progress).
  const [percent, setPercent] = useState(0);
  const [progressMessage, setProgressMessage] = useState("");
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadFileName, setUploadFileName] = useState("");
  const [dragOver, setDragOver] = useState(false);
  const [expandedErrors, setExpandedErrors] = useState<Set<number>>(new Set());
  const [busyRows, setBusyRows] = useState<Set<number>>(new Set());
  const [rowErrors, setRowErrors] = useState<Record<number, string>>({});
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [preview, setPreview] = useState<RunVersionPreview | null>(null);

  // Safe CSV is gated on Ingest Configuration server-side; Upload Customer lacks it.
  const isInternal =
    appStore.loggedInUser?.permissions?.hasAccess?.("Ingest Configuration") ?? false;

  // s3_uri is stamped at presign, so an upload_failed run names bytes that never landed.
  const hasSafeCsv = (file: IngestRun) =>
    isInternal && !!file.s3_uri && file.status !== "upload_failed";

  // An Upload Customer can reach this page too (`configs` matches on Config List OR
  // Ingest Files), so the MPN-1820 lock belongs here as well as on the Upload page: a
  // customer loses Discard once the file has been submitted for processing, and the
  // server returns 403 on the same statuses. Disabled rather than hidden, so the reason
  // is on the control they go looking for.
  const discardLockReason = (status: IngestRunStatus): string | null =>
    !isInternal && status === "completed"
      ? "This file has already been submitted for processing and can no longer be " +
        "removed. Contact your Motimatic representative if it needs to be reversed."
      : null;

  // Server-scoped to this definition — clicking a config shows that config's
  // runs, not the org's whole history. Pre-FK limbo uploads (rows never stamped
  // with a definition) only surface in org-wide views like the Data File host.
  const loadFiles = useCallback(async () => {
    setFiles(await fileService.listFiles(organizationId, definitionId));
  }, [organizationId, definitionId]);

  useEffect(() => {
    if (!organizationId) return;
    void loadFiles();
  }, [organizationId, loadFiles]);




  // One poller for both buttons AND for a job this panel didn't start. Adoption is
  // the point: the indicator used to live only in the tab that clicked, so a page
  // reload during a 15-minute batch showed nothing at all (MPN-1490).
  const pollJob = useCallback(
    (jobId: number, kind: "pending" | "reprocess", since: number) => {
      setProcessKind(kind);
      setStartedAt(since);
      const cmd = new CommandGetJob();
      stopPolling();
      pollRef.current = setInterval(async () => {
        try {
          const job = await cmd.run(jobId);
          setProcessJob({ id: jobId, status: job.status });
          setPercent(job.percent_complete ?? 0);
          setProgressMessage(job.progress_message ?? "");
          await loadFiles();
          if (JOB_TERMINAL.includes(job.status)) {
            stopPolling();
            setProcessKind(null);
            setStartedAt(null);
            setPercent(0);
            setProgressMessage("");
            onMutated?.();
          }
        } catch {
          stopPolling();
          setProcessMessage("Lost track of the job — check the job page.");
        }
      }, JOB_POLL_MS);
    },
    [loadFiles, onMutated],
  );

  // On mount, adopt any batch already running for this definition.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const jobs = await new CommandListJobs().run(
          20, 0,
          ["PROCESS_PENDING_FILES", "RESET_SEGMENT_DATA"],
          ["CREATED", "PENDING", "ACTIVE"],
        );
        const mine = jobs.find((j) => j.input?.ingest_definition_id === definitionId);
        if (!mine || cancelled) return;
        setProcessJob({ id: mine.id, status: mine.status });
        pollJob(
          mine.id,
          mine.type === "RESET_SEGMENT_DATA" ? "reprocess" : "pending",
          new Date(mine.create_date).getTime(),
        );
      } catch {
        /* the indicator is a nicety — never block the panel on it */
      }
    })();
    return () => { cancelled = true; };
  }, [definitionId, pollJob]);

  const handleUpload = async (file: File) => {
    if (!organizationId) return;

    const preflightError = preflightUploadFile(file);
    if (preflightError) {
      setUploadError(preflightError);
      return;
    }

    setUploadError(null);
    setUploading(true);
    setUploadProgress(0);
    setUploadFileName(file.name);

    let newRunId: number | undefined;
    try {
      let presign;
      try {
        // definitionId files the upload under this definition's etl/<ingest_dir>/
        // subtree and stamps the run's FK at presign (MPN-1445).
        presign = await fileService.presignUpload(
          organizationId,
          file.name,
          file.type || "text/csv",
          definitionId,
        );
      } catch (err) {
        throw new Error(describePresignFailure(err));
      }
      newRunId = presign.id;

      // Upload to S3 via presigned PUT URL using XMLHttpRequest for progress
      await new Promise<void>((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open("PUT", presign.url, true);
        xhr.setRequestHeader("Content-Type", file.type || "text/csv");
        // Without a timeout a hung connection leaves the spinner up forever.
        xhr.timeout = 120000;

        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable) {
            setUploadProgress(Math.round((e.loaded / e.total) * 100));
          }
        };

        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) resolve();
          else reject(new Error(describeStorageFailure(xhr)));
        };
        xhr.onerror = () => reject(new Error("Couldn't reach file storage. Check your connection and try again."));
        xhr.ontimeout = () => reject(new Error("The upload timed out reaching file storage. Please try again."));
        xhr.send(file);
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : "Upload failed";
      // The run row is created at presign, before the bytes transfer. If that transfer
      // failed, mark the run so the list shows the failure instead of a phantom
      // "Uploaded" row for a file that never landed (MPN-1345).
      if (newRunId !== undefined) {
        try {
          await fileService.markUploadFailed(newRunId, message);
        } catch {
          /* best-effort — the banner still surfaces the failure */
        }
        await loadFiles();
        onMutated?.();
      }
      setUploadError(message);
      setUploading(false);
      setUploadProgress(0);
      setUploadFileName("");
      return;
    }

    // S3 upload finished -- leave the "Uploading" state before validating so a
    // slow or failing validate reads as row-level activity, not a stuck upload.
    setUploading(false);
    setUploadProgress(0);
    setUploadFileName("");
    await loadFiles();
    onMutated?.();
    await handleValidate(newRunId!);
  };

  // Sequential on purpose: parallel uploads would interleave progress bars and
  // presign/validate calls; a batch of historical files isn't latency-sensitive.
  const uploadMany = async (selected: File[]) => {
    for (const file of selected) {
      await handleUpload(file);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const selected = Array.from(e.dataTransfer.files);
    if (selected.length) void uploadMany(selected);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = Array.from(e.target.files ?? []);
    if (selected.length) void uploadMany(selected);
    e.target.value = "";
  };

  const handleValidate = async (fileId: number) => {
    setBusyRows((prev) => new Set(prev).add(fileId));
    try {
      const result = await fileService.validateFile(fileId, definitionId);
      if (result.errors?.length || result.warnings?.length) {
        setExpandedErrors((prev) => new Set(prev).add(fileId));
      } else {
        setExpandedErrors((prev) => {
          const next = new Set(prev);
          next.delete(fileId);
          return next;
        });
      }
      await loadFiles();
      onMutated?.();
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : "Validation request failed");
    } finally {
      setBusyRows((prev) => {
        const next = new Set(prev);
        next.delete(fileId);
        return next;
      });
    }
  };

  const handleRun = async (fileId: number) => {
    setRowErrors((prev) => { const next = { ...prev }; delete next[fileId]; return next; });
    setBusyRows((prev) => new Set(prev).add(fileId));
    try {
      await fileService.runFile(fileId);
      await loadFiles();
      onMutated?.();
    } catch (err: any) {
      const msg = err?.message || "Run failed";
      setRowErrors((prev) => ({ ...prev, [fileId]: msg }));
    } finally {
      setBusyRows((prev) => {
        const next = new Set(prev);
        next.delete(fileId);
        return next;
      });
    }
  };

  // One history for the whole population, not one per row. The question asked on this tab is
  // "what has been happening to the data feeding this campaign", and answering it from 48
  // separate drawers is the archaeology the history exists to remove — each entry names its
  // own file instead.
  //
  // Always refetched on open: validating or discarding a file changes the history, and a
  // cached list would show it as it stood before the thing the user just did.
  const openHistory = async () => {
    setHistoryOpen(true);
    setVersions(null);
    setActivity([]);
    setHistoryError(false);
    try {
      const history = await new IngestFileService().getDefinitionFileHistory(definitionId);
      setVersions(history.versions);
      setActivity(history.activity);
    } catch {
      setVersions([]);
      setHistoryError(true);
    }
  };

  const handleDiscard = async (fileId: number, status: IngestRunStatus) => {
    const message =
      status === "completed"
        ? "Discard this processed file?\n\n" +
          "It leaves the list and every future rebuild: Reprocess all files will skip it. " +
          "The data it already loaded stays until the population is rebuilt or reset.\n\n" +
          "To replace a wrong file: discard it, upload the corrected one, then Reprocess all files. " +
          "The file itself is kept in the discarded folder, not deleted."
        : "Discard this file? It will disappear from the list and won't be processed. " +
          "The file itself is kept in the discarded folder.";
    if (!window.confirm(message)) return;
    setRowErrors((prev) => { const next = { ...prev }; delete next[fileId]; return next; });
    setBusyRows((prev) => new Set(prev).add(fileId));
    try {
      await fileService.discardFile(fileId);
      await loadFiles();
      onMutated?.();
    } catch (err: any) {
      setRowErrors((prev) => ({ ...prev, [fileId]: err?.message || "Discard failed" }));
    } finally {
      setBusyRows((prev) => {
        const next = new Set(prev);
        next.delete(fileId);
        return next;
      });
    }
  };

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  useEffect(() => stopPolling, [stopPolling]);

  const JOB_TERMINAL = ["COMPLETED", "FAILED", "WARNING"];
  const JOB_POLL_MS = 2000;

  // Ops bulk processing (MPN-1445): validate + load every uploaded file for this
  // definition in file-date order — the browser-button version of catch_up_launch.
  // Polls the job until it finishes so the rows update live (same pattern as the
  // publish Network View panel), rather than telling the user to refresh.
  const handleProcessPending = async () => {
    setProcessing(true);
    setProcessJob(null);
    setProcessMessage(null);
    setProcessKind("pending");
    setStartedAt(Date.now());
    stopPolling();
    try {
      const { job_id } = await fileService.processPendingFiles(definitionId);
      setProcessJob({ id: job_id, status: "CREATED" });
      await loadFiles();
      pollJob(job_id, "pending", Date.now());
    } catch (err) {
      setProcessMessage(err instanceof Error ? err.message : "Bulk processing failed to start");
    } finally {
      setProcessing(false);
    }
  };

  // Ops parity loop (MPN-1490): re-run every already-loaded file in file-date
  // order, so counts can be compared against the old platform, adjusted, and
  // compared again. Deliberately NOT "process pending" — that skips COMPLETED
  // files, so a reset followed by it never re-traces the earliest date and
  // silently turns arrival-state pre-converts into billable conversions
  // (MPN-1491). The server resolves how far to replay and does reset + replay.
  const reprocess = useReprocessAll({
    definitionId,
    populationName,
    launches,
    onStarted: (jobId, message) => {
      setProcessJob({ id: jobId, status: "CREATED" });
      setProcessMessage(message);
      void loadFiles();
      pollJob(jobId, "reprocess", Date.now());
    },
    onError: setProcessMessage,
  });
  const { rebuildScope, undatedWarning } = reprocess;

  const handleDownload = async (file: IngestRun) => {
    try {
      const { url } = await fileService.downloadFile(file.id);
      const a = document.createElement("a");
      a.href = url;
      a.download = file.file_name ?? "";
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (err) {
      console.error("Download error:", err);
    }
  };

  const handleVerifyDownload = async (fileId: number) => {
    setRowErrors((prev) => { const next = { ...prev }; delete next[fileId]; return next; });
    setBusyRows((prev) => new Set(prev).add(fileId));
    try {
      await fileService.verifyDownload(fileId);
    } catch (err: any) {
      setRowErrors((prev) => ({ ...prev, [fileId]: err?.message || "Verify download failed" }));
    } finally {
      setBusyRows((prev) => {
        const next = new Set(prev);
        next.delete(fileId);
        return next;
      });
    }
  };

  const handlePreview = async (runId: number) => {
    setPreviewOpen(true);
    setPreviewLoading(true);
    setPreview(null);
    try {
      setPreview(await fileService.previewVersions(runId));
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : "Preview failed");
      setPreviewOpen(false);
    } finally {
      setPreviewLoading(false);
    }
  };

  const toggleExpanded = (fileId: number) => {
    setExpandedErrors((prev) => {
      const next = new Set(prev);
      if (next.has(fileId)) next.delete(fileId);
      else next.add(fileId);
      return next;
    });
  };

  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr);
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
      + " " + d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
  };

  // Bare file dates ("2026-07-08"): parsed with a local midnight so the day reads
  // as written rather than shifting a day west of UTC.
  const formatDay = (value: string) =>
    new Date(`${value}T00:00:00`).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });

  const formatRowCount = (count: number | null) => {
    if (count == null) return "—";
    return count.toLocaleString();
  };

  const jobRunning = processJob != null && !JOB_TERMINAL.includes(processJob.status);
  // The job row carries no progress (PROCESS_PENDING_FILES/RESET_SEGMENT_DATA never
  // call update_job_progress and spawn no child jobs), so derive it from the file
  // list we already re-poll every tick — the row currently mid-load is real progress.
  const elapsed = jobRunning && startedAt ? Math.round((Date.now() - startedAt) / 1000) : 0;
  const elapsedText = elapsed >= 60 ? `${Math.floor(elapsed / 60)}m ${elapsed % 60}s` : `${elapsed}s`;
  const runningVerb = processKind === "reprocess" ? "Rebuilding" : "Processing";
  const jobFailed = processJob?.status === "FAILED";
  const jobLink = processJob ? (
    <Link to={`${import.meta.env.BASE_URL}${Paths.JOBS}/${processJob.id}`}>
      job #{processJob.id}
    </Link>
  ) : null;
  // One status line, not two stacked side by side: while a batch runs, the headline
  // the action set ("Rebuilding everything through …") is more specific than the
  // generic file-order explainer, and the file/percent/clock detail belongs under
  // the bar. The explainer only earns the line when there's nothing better to say.
  const statusLine = !processJob
    ? processMessage
    : jobRunning
      ? (
        <>
          {processMessage || "Processing files in file-date order — statuses update as each one loads."}
          {" · "}{jobLink}
        </>
      )
      : jobFailed
        ? <>Processing failed — open {jobLink} for details.</>
        : processJob.status === "WARNING"
          ? <>Processing finished with warnings — see {jobLink} for details.</>
          : <>Processing finished ({jobLink}).</>;

  return (
    <Box sx={{ p: 3 }}>
      {headerSlot}

      {/* Ops: bulk-process everything uploaded, in file-date order (MPN-1445).
          Controls only — the job status lives on its own full-width line below.
          Sharing the row with a sentence let flexbox shrink the buttons into an
          unreadable grey stub the moment a batch started. */}
      <Box sx={{ mb: 2, display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1.5 }}>
        <Button
          variant="outlined"
          size="small"
          onClick={handleProcessPending}
          disabled={processing || uploading || jobRunning || !hasPendingRuns(files)}
          title={
            hasPendingRuns(files)
              ? "Validates every uploaded file first (one bad file stops the batch before anything loads), then processes them in file-date order."
              : "Nothing pending — every file here has already been processed. Upload a file to enable this."
          }
          sx={{ textTransform: "none", fontSize: 12, flexShrink: 0, whiteSpace: "nowrap" }}
        >
          {processKind === "pending" && processing
            ? "Starting…"
            : processKind === "pending" && jobRunning
              ? "Processing…"
              : "Process pending files"}
        </Button>
        {!hideReprocessAll && (
        <Button
          variant="outlined"
          size="small"
          onClick={reprocess.start}
          disabled={processing || reprocess.starting || uploading || jobRunning || !hasRunnableRuns(files)}
          title={
            `Re-run every stored file for ${populationLabel}, oldest first — from every data spec ` +
            `that feeds it, not just this one. ${rebuildScope} Use this to re-check the numbers ` +
            "after changing a field map or a rule."
          }
          sx={{ textTransform: "none", fontSize: 12, flexShrink: 0, whiteSpace: "nowrap" }}
        >
          {reprocess.starting
            ? "Starting…"
            : processKind === "reprocess" && jobRunning
              ? "Rebuilding…"
              : `Reprocess all files for ${populationLabel}`}
        </Button>
        )}
        {/* Pushed to the right, the way the field map's Versions button sits opposite its
            title: these two are things you DO to the files, and history is something you
            READ about them. */}
        <Box sx={{ flexGrow: 1 }} />
        <HistoryButton
          onClick={openHistory}
          title="What has happened to this population's files — every file, one timeline"
        />
      </Box>

      {/* What the button will do to THIS population, on the page — not in a tooltip
          nobody hovers, and not in the status banner after it has started (MPN-1817). */}
      {!hideReprocessAll && (
      <Typography sx={{ fontSize: 12, color: "#8c9097", mb: 2, mt: -1 }}>
        Reprocess all files re-runs every stored file for {populationLabel}, oldest first, under
        the current field map and rules. {rebuildScope}
      </Typography>
      )}

      {!hideReprocessAll && undatedWarning && (
        <Alert severity="warning" sx={{ mb: 2, fontSize: 12 }}>
          {undatedWarning}
        </Alert>
      )}

      {/* The billing boundary is each campaign's go-live date — stamped by its first
          publish, never typed here (MPN-1675). Files dated before it load as arrival
          state and don't bill; files after it track conversions. A campaign that is
          live with no date is the one state worth shouting about: every conversion
          in its files bills until the date is set. */}
      {launches && (
        <Box sx={{ mb: 2, mt: -1, display: "flex", flexWrap: "wrap", gap: 0.5, alignItems: "baseline" }}>
          <Typography sx={{ fontSize: 12, color: "#8c9097", mr: 0.5 }}>Campaign go-live:</Typography>
          {launches.length === 0 && (
            <Typography
              component="span"
              title="Link a campaign to this population on the Links tab. Until one is live, files load as arrival state and nothing bills."
              sx={{ fontSize: 12, color: "#8c9097", fontStyle: "italic" }}
            >
              no campaign is linked to this population yet
            </Typography>
          )}
          {launches.map((l) => {
            const undatedLive = l.is_live && !l.go_live_date;
            return (
              <Typography
                key={l.id}
                component="span"
                title={
                  undatedLive
                    ? "This campaign is live on a network but has no go-live date, so its files load as arrival state and nothing bills until the date is set."
                    : l.go_live_date
                      ? "Files dated before this load as arrival state and don't bill; files after it track conversions."
                      : "Not published yet — files load as arrival state until the campaign goes live."
                }
                sx={{
                  fontSize: 12,
                  color: undatedLive ? "#e6533c" : "#536485",
                  fontWeight: undatedLive ? 600 : 400,
                  bgcolor: undatedLive ? "rgba(230,83,60,0.08)" : "#f1f5f9",
                  borderRadius: 1,
                  px: 1,
                  py: 0.25,
                }}
              >
                {l.name}: {l.go_live_date ? formatDay(l.go_live_date) : undatedLive ? "live, no go-live date" : "not live yet"}
              </Typography>
            );
          })}
        </Box>
      )}

      {/* A long batch is minutes of apparent silence otherwise: the buttons grey out
          and nothing else moves. Indeterminate on purpose when the job reports no
          percentage — but the file name and the clock are real and keep advancing,
          which is what tells an operator it's alive rather than wedged. Headline,
          bar, then detail: one column at full width, so nothing gets clipped. */}
      {(statusLine || jobRunning) && (
        <Box sx={{ mb: 2 }}>
          {statusLine && (
            <Typography
              sx={{
                fontSize: 13,
                color: jobFailed ? "#e6533c" : "#536485",
                mb: jobRunning ? 0.75 : 0,
              }}
            >
              {statusLine}
            </Typography>
          )}
          {jobRunning && (
            <>
              <LinearProgress
                variant={percent > 0 ? "determinate" : "indeterminate"}
                value={percent}
                sx={{ height: 4, borderRadius: 2, mb: 0.75 }}
              />
              <Typography sx={{ fontSize: 12, color: "#536485" }}>
                {progressMessage || `${runningVerb}…`}
                {percent > 0 ? ` · ${Math.round(percent)}%` : ""}
                {` · ${elapsedText} elapsed`}
              </Typography>
            </>
          )}
        </Box>
      )}

      {uploadError && (
        <Alert severity="error" onClose={() => setUploadError(null)} sx={{ mb: 2 }}>
          {uploadError}
        </Alert>
      )}

      {/* Drop Zone */}
      <Box
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragEnter={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        onClick={() => !uploading && fileInputRef.current?.click()}
        sx={{
          border: uploading
            ? "2px solid #845adf"
            : dragOver
              ? "2px solid #845adf"
              : "2px dashed #e9edf6",
          borderRadius: 2,
          p: 3,
          textAlign: "center",
          bgcolor: uploading || dragOver ? "rgba(132,90,223,0.03)" : "#f9fafb",
          mb: 2,
          cursor: uploading ? "default" : "pointer",
          transition: "border-color 0.2s, background-color 0.2s",
        }}
      >
        {uploading ? (
          <>
            <Typography fontWeight={500} mb={1}>Uploading {uploadFileName}...</Typography>
            <Box sx={{ maxWidth: 300, mx: "auto" }}>
              <LinearProgress variant="determinate" value={uploadProgress} sx={{ height: 6, borderRadius: 1 }} />
            </Box>
            <Typography sx={{ color: "#8c9097", fontSize: 13, mt: 0.5 }}>{uploadProgress}%</Typography>
          </>
        ) : (
          <>
            <Typography sx={{ fontSize: 22, opacity: 0.4, mb: 0.5 }}>&#128196;</Typography>
            <Typography fontWeight={500} sx={{ color: "#536485" }}>Drop CSV files here or click to browse</Typography>
            <Typography sx={{ color: "#8c9097", fontSize: 13 }}>Files will be uploaded to S3 for processing</Typography>
          </>
        )}
        <input
          ref={fileInputRef}
          type="file"
          accept=".csv"
          multiple
          style={{ display: "none" }}
          onChange={handleFileSelect}
        />
      </Box>

      {/* File Table */}
      <TableContainer sx={{ bgcolor: "#fff", borderRadius: 2, border: "1px solid #f3f3f3" }}>
        <Table size="small" sx={liftableTableSx}>
          <TableHead>
            <TableRow sx={{ bgcolor: "#f9fafb" }}>
              <TableCell sx={{ fontWeight: 600, color: "#8c9097", fontSize: 12, textTransform: "uppercase", letterSpacing: 0.3 }}>File</TableCell>
              <TableCell sx={{ fontWeight: 600, color: "#8c9097", fontSize: 12, textTransform: "uppercase", letterSpacing: 0.3 }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 600, color: "#8c9097", fontSize: 12, textTransform: "uppercase", letterSpacing: 0.3 }}>Method</TableCell>
              <TableCell sx={{ fontWeight: 600, color: "#8c9097", fontSize: 12, textTransform: "uppercase", letterSpacing: 0.3 }}>Rows</TableCell>
              <TableCell sx={{ fontWeight: 600, color: "#8c9097", fontSize: 12, textTransform: "uppercase", letterSpacing: 0.3 }}>Date</TableCell>
              <TableCell sx={{ fontWeight: 600, color: "#8c9097", fontSize: 12, textTransform: "uppercase", letterSpacing: 0.3 }} align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {files.map((file) => {
              const isBusy = busyRows.has(file.id);
              const discardLock = discardLockReason(file.status);
              const rebuildOnly = needsRebuild(file, files);
              // Status detail is persisted by the backend (validate + run), so it
              // survives navigating away and back and is present for successful
              // runs too, not just failures (MPN-1345).
              // The load summary's warnings sit with the validation ones; its per-column
              // contact counts get their own list (MPN-1969).
              const contactCounts = file.load_summary?.contacts ?? [];
              const loadWarned = hasLoadWarnings(file);
              const sd = {
                message: file.status_message ?? "",
                errors: file.status_errors ?? [],
                warnings: [...(file.status_warnings ?? []), ...(file.load_summary?.warnings ?? [])],
              };
              const isExpanded = expandedErrors.has(file.id);
              const hasDetail = !!(sd.message || sd.errors.length || sd.warnings.length || contactCounts.length);

              return (
                <React.Fragment key={file.id}>
                  <TableRow
                    onClick={hasDetail ? () => toggleExpanded(file.id) : undefined}
                    sx={[hasDetail && rowHoverLiftSx, { cursor: hasDetail ? "pointer" : "default" }]}
                  >
                    <TableCell sx={{ fontWeight: 500 }}>
                      <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
                        <span>{file.file_name ?? "—"}</span>
                        {file.s3_uri && (
                          <Box
                            component="a"
                            onClick={(e) => { e.stopPropagation(); handleDownload(file); }}
                            title="Download file"
                            sx={{
                              cursor: "pointer",
                              color: "#b5bac1",
                              display: "inline-flex",
                              "&:hover": { color: "#845adf" },
                            }}
                          >
                            <DownloadIcon sx={{ fontSize: 14 }} />
                          </Box>
                        )}
                        {file.is_test_file && (
                          <Box
                            component="span"
                            title="This file carries an is_test column, so the rows it loads are inert — never served, billed, or reported."
                            sx={{
                              display: "inline-flex", alignItems: "center", gap: 0.25,
                              px: 0.75, py: 0.125, borderRadius: 5, bgcolor: "#fdf3e4",
                              color: "#b06f16", fontSize: 11, fontWeight: 500,
                              // The filename column is the one that gives, not the chip.
                              whiteSpace: "nowrap", flexShrink: 0,
                            }}
                          >
                            <ScienceIcon sx={{ fontSize: 12 }} />
                            Test file
                          </Box>
                        )}
                        {file.config_version && (
                          <Box
                            component="span"
                            title="Config version this run executed against"
                            sx={{
                              px: 0.75, py: 0.125, borderRadius: 5, bgcolor: "#f1ecfb",
                              color: "#845adf", fontSize: 11, fontWeight: 500,
                            }}
                          >
                            v{file.config_version.version_number}
                          </Box>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        {/* A parked file sits at 'validated' like any pre-load file, so
                            without this it reads as ready-to-run and looks stuck when
                            nobody runs it (MPN-1564). */}
                        <span
                          style={{
                            color: file.waiting_on || loadWarned ? "#f5b849" : STATUS_COLORS[file.status],
                            fontSize: 13,
                            fontWeight: 500,
                          }}
                          title={
                            file.waiting_on?.startsWith("data issue")
                              ? `Held by ${file.waiting_on}: loads once it is accepted or corrected (Data issues).`
                              : file.waiting_on
                                ? `Loads automatically once "${file.waiting_on}" has been processed.`
                                : undefined
                          }
                        >
                          {file.waiting_on
                            ? `Waiting on ${file.waiting_on}`
                            : loadWarned ? "Completed with warnings" : STATUS_LABELS[file.status]}
                        </span>
                        {hasDetail && (
                          <Box
                            component="a"
                            onClick={(e) => { e.stopPropagation(); toggleExpanded(file.id); }}
                            sx={{ color: "#8c9097", fontSize: 12, cursor: "pointer", textDecoration: "none", "&:hover": { textDecoration: "underline" } }}
                          >
                            {isExpanded ? "Hide details" : "Details"}
                          </Box>
                        )}
                      </Box>
                    </TableCell>
                    {/* How the file reached us (MPN-1634). Ops was inferring it from
                        whether the client had an SFTP key on another tab, which is a
                        proxy for "could have used SFTP", not for "did". */}
                    <TableCell>
                      <span style={{ fontSize: 13, color: "#5c6067" }}>
                        {INGEST_SOURCE_LABELS[file.source] ?? file.source}
                      </span>
                    </TableCell>
                    <TableCell>
                      {formatRowCount(file.row_count)}
                      {/* Only a dependent file has these. The dropped rows were never
                          stored, so the plain row count alone overstates what loaded. */}
                      {file.unmatched_row_count != null && file.unmatched_row_count > 0 && (
                        <Box
                          sx={{ color: "#f5b849", fontSize: 12 }}
                          title={
                            `${file.unmatched_row_count} row(s) were for people not in this ` +
                            `population and were not loaded. If they should have been, the ` +
                            `file this one depends on needs re-sending with them included.`
                          }
                        >
                          {formatRowCount(file.unmatched_row_count)} not in population
                        </Box>
                      )}
                    </TableCell>
                    <TableCell sx={{ color: "#8c9097" }}>{formatDate(file.create_date)}</TableCell>
                    <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                      <Box sx={{ display: "flex", gap: 0.75, justifyContent: "flex-end" }}>
                        {file.config_version && (
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => handlePreview(file.id)}
                            title="See how this file's columns are read under its pinned config vs current"
                            sx={{ textTransform: "none", fontSize: 12, borderColor: "#e0e2e9", color: "#536485" }}
                          >
                            Preview
                          </Button>
                        )}
                        <Button
                          variant="contained"
                          size="small"
                          disabled={isBusy}
                          onClick={() => handleValidate(file.id)}
                          sx={{ textTransform: "none", fontSize: 12, minWidth: 70 }}
                        >
                          Validate
                        </Button>
                        {rebuildOnly ? (
                          <Typography
                            component="span"
                            title="Running it on its own would be refused: its date is older than the latest processed file, and loading it out of order would corrupt the conversion log. Use Reprocess all files, which rebuilds from every stored file in order and includes this one."
                            sx={{ fontSize: 12, color: "#8c9097", alignSelf: "center", whiteSpace: "nowrap" }}
                          >
                            Runs with Reprocess all files
                          </Typography>
                        ) : (
                          <Button
                            variant="contained"
                            size="small"
                            color="success"
                            disabled={isBusy || file.status !== "validated" || Boolean(file.waiting_on)}
                            title={
                              file.waiting_on
                                ? `Waiting on "${file.waiting_on}" — this file loads by itself once that one has been processed.`
                                : undefined
                            }
                            onClick={() => handleRun(file.id)}
                            sx={{ textTransform: "none", fontSize: 12, minWidth: 50 }}
                          >
                            Run
                          </Button>
                        )}
                        {file.status !== "running" && file.status !== "validating" && (
                          <Button
                            variant="outlined"
                            size="small"
                            disabled={isBusy || !!discardLock}
                            title={
                              discardLock ??
                              "Remove this file from the list; the file is kept in the discarded folder. For a processed file: discard, upload the corrected file, then Reprocess all files (discard clears the row, not the data it loaded)."
                            }
                            onClick={() => handleDiscard(file.id, file.status)}
                            sx={{ textTransform: "none", fontSize: 12, borderColor: "#e6533c", color: "#e6533c" }}
                          >
                            Discard
                          </Button>
                        )}
                        {/* Shield-check = the cleaned (PII-free) download, vs the plain
                            arrow by the filename which fetches the raw file. Icon-only,
                            and rendered on every row (hidden when unavailable) so the
                            action buttons line up down the column. Nothing is generated:
                            it streams from the source file, so it is available from the
                            moment that lands rather than when the run completes (MPN-1583).
                            TODO(PII split): one download per user — raw for
                            PII-cleared staff, safe CSV for everyone else. */}
                        <Button
                          variant="outlined"
                          size="small"
                          disabled={isBusy}
                          onClick={() => handleVerifyDownload(file.id)}
                          title={
                            file.status === "completed"
                              ? "Download Safe CSV — a PII-free copy with consumer IDs in place of your external IDs, to reconcile against the population screen"
                              : "Download Safe CSV — a PII-free copy of this file. Consumer IDs are blank until the file has been processed, because the people don't exist yet."
                          }
                          sx={{
                            textTransform: "none",
                            fontSize: 12,
                            minWidth: 34,
                            px: 1,
                            borderColor: "#845adf",
                            color: "#845adf",
                            visibility: hasSafeCsv(file) ? "visible" : "hidden",
                          }}
                        >
                          <GppGoodIcon sx={{ fontSize: 16 }} />
                        </Button>
                      </Box>
                      {rowErrors[file.id] && (
                        <Typography sx={{ color: "#e6533c", fontSize: 12, mt: 0.5 }}>
                          {rowErrors[file.id]}
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                  {hasDetail && isExpanded && (
                    <TableRow>
                      <TableCell
                        colSpan={5}
                        sx={{
                          p: "4px 14px 14px 14px",
                          bgcolor: sd.errors.length ? "rgba(230,83,60,0.02)" : "#fafafb",
                        }}
                      >
                        <Box sx={{ bgcolor: "#fff", border: "1px solid #f3f3f3", borderRadius: 1.5, p: 2 }}>
                          {sd.message && (
                            <Typography
                              sx={{
                                fontWeight: 600,
                                fontSize: 13,
                                color: STATUS_COLORS[file.status],
                                mb: sd.errors.length || sd.warnings.length || contactCounts.length ? 1.25 : 0,
                              }}
                            >
                              {sd.message}
                            </Typography>
                          )}
                          {sd.errors.length > 0 && (
                            <>
                              <Typography sx={{ fontWeight: 600, color: "#e6533c", fontSize: 13, mb: 0.5 }}>Errors</Typography>
                              <Box component="ul" sx={{ m: 0, pl: 2.5, color: "#536485", fontSize: 13, lineHeight: 1.8, mb: sd.warnings.length ? 1.5 : 0 }}>
                                {sd.errors.map((err, i) => (
                                  <li key={i}>{err}</li>
                                ))}
                              </Box>
                            </>
                          )}
                          {contactCounts.length > 0 && (
                            <>
                              <Typography sx={{ fontWeight: 600, color: "#536485", fontSize: 13, mb: 0.5 }}>Contact points loaded</Typography>
                              <Box component="ul" sx={{ m: 0, pl: 2.5, color: "#536485", fontSize: 13, lineHeight: 1.8, mb: sd.warnings.length ? 1.5 : 0 }}>
                                {contactCounts.map((c) => (
                                  <li key={`${c.kind}:${c.column}`} style={c.written === 0 ? { color: "#e6533c" } : undefined}>
                                    {CONTACT_KIND_LABELS[c.kind] ?? c.kind}: {c.written.toLocaleString()} of{" "}
                                    {c.rows.toLocaleString()} rows ({c.column})
                                  </li>
                                ))}
                              </Box>
                            </>
                          )}
                          {sd.warnings.length > 0 && (
                            <>
                              <Typography sx={{ fontWeight: 600, color: "#f5b849", fontSize: 13, mb: 0.5 }}>Warnings</Typography>
                              <Box component="ul" sx={{ m: 0, pl: 2.5, color: "#536485", fontSize: 13, lineHeight: 1.8 }}>
                                {sd.warnings.map((warn, i) => (
                                  <li key={i}>{warn}</li>
                                ))}
                              </Box>
                            </>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
        {files.length === 0 && !uploading && (
          <Box sx={{ p: 4, textAlign: "center", color: "#8c9097" }}>
            <Typography>No files uploaded yet. Drop a CSV above to get started.</Typography>
          </Box>
        )}
      </TableContainer>

      <RunVersionPreviewDialog
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        loading={previewLoading}
        preview={preview}
      />

      {reprocess.dialog}

      <AggregateVersionsDrawer
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        versions={versions ?? []}
        activity={activity}
        title="File history"
        subtitle="Every file in this population"
        triggerLabels={FILE_TRIGGER_LABELS}
        emptyMessage={
          historyError
            ? "Could not load the file history."
            : versions === null
              ? "Loading…"
              : "Nothing recorded yet. Uploading, validating or processing a file creates the first entry."
        }
      />
    </Box>
  );
}

// ---- Route wrapper ----

export default function IngestFilesPage() {
  useDocumentTitle("Ingest Files");
  const { configId } = useParams<{ configId: string }>();
  const navigate = useNavigate();
  const definitionId = Number(configId);

  const [definition, setDefinition] = useState<DefinitionInfo | null>(null);

  useEffect(() => {
    if (!definitionId) return;
    new IngestDefinitionService().fetchDefinitions().then((defs) => {
      const def = defs.find((d: { id: number }) => d.id === definitionId);
      if (!def) return;
      setDefinition({
        id: def.id,
        name: def.name,
        organization_id: def.organization?.id ?? null,
        population_name: def.population_segment?.name ?? def.population_segment_name ?? null,
      });
    });
  }, [definitionId]);

  if (!definition?.organization_id) {
    return (
      <Box sx={{ p: 3 }}>
        <LinearProgress />
      </Box>
    );
  }

  return (
    <IngestFilesPanel
      definitionId={definitionId}
      organizationId={definition.organization_id}
      populationName={definition.population_name ?? undefined}
      headerSlot={
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Box
              component="a"
              onClick={() => navigate("/configs")}
              sx={{
                color: "#845adf",
                cursor: "pointer",
                textDecoration: "none",
                display: "inline-flex",
                alignItems: "center",
                fontSize: 14,
                "&:hover": { textDecoration: "underline" },
              }}
            >
              <ArrowBackIosNewIcon sx={{ fontSize: 12, mr: 0.5 }} />
              Back to Definitions
            </Box>
            <Typography sx={{ color: "#8c9097", mx: 1 }}>&middot;</Typography>
            <Typography variant="h6" fontWeight={600}>{definition.name}</Typography>
          </Box>
          <Button
            variant="outlined"
            size="small"
            onClick={() => navigate(`/configs/${definitionId}/field-map`)}
            sx={{ textTransform: "none", borderColor: "#845adf", color: "#845adf" }}
          >
            Field Map
          </Button>
        </Box>
      }
    />
  );
}
